import React from 'react';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import Box from '@mui/material/Box';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import FormHelperText from '@mui/material/FormHelperText';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { useAppSelector } from 'redux/hooks/reduxHooks';
import { select12HourFormat } from 'redux/selectors/selectors';

export type TControlledInputProps<T extends FieldValues> = DatePickerProps<T> &
  UseControllerProps<T> & {
    errorMessage: string;
  };

export const DatePickerWrapper = <T extends FieldValues>({
  name,
  control,
  errorMessage,
  ...props
}: TControlledInputProps<T>) => {
  const { field } = useController<T>({ control, name });
  const is12HourFormat = useAppSelector(select12HourFormat);

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          {...field}
          {...props}
          slots={{
            openPickerIcon: CalendarMonthOutlinedIcon,
          }}
          format={is12HourFormat ? 'MM.DD.YYYY' : 'DD.MM.YYYY'}
          slotProps={{
            textField: {
              variant: 'filled',
              sx: {
                width: '100%',
              },
              inputProps: {
                sx: {
                  padding: '9px 0 9px 12px',
                },
              },
              InputProps: {
                disableUnderline: true,
                sx: {
                  borderRadius: { lg: '8px', md: '6px', sm: '6px', xs: '6px' },
                  minHeight: { lg: '44px', md: '46px', sm: '46px', xs: '46px' },
                  border: '1px solid',
                  borderColor: errorMessage.length > 1 ? 'error.main' : 'transparent',
                },
              },
            },
            openPickerButton: {
              sx: (theme) => ({
                color: theme.palette.primary.main,
              }),
            },
          }}
        />
      </LocalizationProvider>
      <FormHelperText>{errorMessage}</FormHelperText>
    </Box>
  );
};
